import { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { pairIntoSessions, getDayRangeUtc, todayDateStringInTz, DAY_ANCHOR_TZ } from '../utils/time';

// Everyone on the team, admins included — they're employees too and check
// in/out the same way VAs do. Shared by the Admin dashboard and the VA
// team-status view so both stay in sync off one computation.
//
// Pass `dateKey` (a CST "YYYY-MM-DD" string, e.g. from todayDateStringInTz)
// to scope this to one specific day — used by the Admin dashboard's date
// filter. Two different things come out of that:
//
//   - `status`/`latest` are carry-forward — "as of the end of that CST
//     day", not "only logs that happened that day". Someone who checked in
//     yesterday and never checked out still correctly shows as 'in' today.
//   - `daySessions` is the full in/out story for that specific day —
//     sessions (an IN paired with its matching OUT) attributed to the CST
//     day of the IN, so a session that started the day before and only
//     closed out this morning stays attributed to its original day rather
//     than showing up as an orphaned OUT here.
export default function useTeamRows(dateKey) {
  const { users, timelogs, leaves } = useApp();

  return useMemo(() => {
    // Same CST day this hook already scopes status/daySessions to when a
    // dateKey is given — falls back to "today" when it isn't (the VA Team
    // view's "right now" case), so On Leave reflects whichever day is
    // actually being looked at.
    const effectiveDateKey = dateKey || todayDateStringInTz(DAY_ANCHOR_TZ);

    // Resigned (Inactive) employees keep their history but drop off every
    // team view — the Employees table (HQ) is the only place they're still
    // listed. Sheets without the status column yet are all-Active, same
    // tolerant default used everywhere else this column is read.
    return users.filter((u) => (u.status || 'Active') !== 'Inactive').map((u) => {
      // Approved leave covering the viewed day, half-day included — this is
      // deliberately a separate flag from `status` (not a replacement for
      // it), so existing in/out/none-keyed consumers (like the Admin
      // dashboard's stat-card counts) are unaffected.
      const onLeave = leaves.some(
        (l) => l.uuid === u.uuid && l.status === 'Approved' && l.date_from <= effectiveDateKey && effectiveDateKey <= l.date_to
      );

      const allLogsForUser = timelogs
        .filter((l) => l.uuid === u.uuid)
        .sort((a, b) => new Date(a.logtime_utc) - new Date(b.logtime_utc));

      let logsUpToEnd = allLogsForUser;
      if (dateKey) {
        const { end } = getDayRangeUtc(dateKey, DAY_ANCHOR_TZ);
        const endMs = end.getTime();
        logsUpToEnd = allLogsForUser.filter((l) => new Date(l.logtime_utc).getTime() <= endMs);
      }

      const latest =
        logsUpToEnd.length === 0
          ? null
          : logsUpToEnd.reduce((best, l) =>
              new Date(l.logtime_utc) > new Date(best.logtime_utc) ? l : best
            );
      const status = !latest ? 'none' : latest.isIn ? 'in' : 'out';

      // An actual check-in beats the leave badge — someone who's on
      // approved leave but came in anyway should show as IN, not On Leave.
      // Being OUT or never-checked-in while on leave still shows On Leave,
      // since that's the more useful signal in both those cases.
      const displayStatus = onLeave && status !== 'in' ? 'leave' : status;

      let daySessions = [];
      if (dateKey) {
        const sessions = pairIntoSessions(allLogsForUser);
        daySessions = sessions.filter((s) => s.dayKey === dateKey);
      }

      return { user: u, latest, status, displayStatus, daySessions, onLeave };
    });
  }, [users, timelogs, leaves, dateKey]);
}
